import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { designSystem } from '@yola/ws-ui';

const {
  Icon,
  Paragraph,
  DropdownContent,
  DropdownTrigger,
  Dropdown,
  ActionList,
  ActionListItem,
  ActionListDivider,
  IconButton,
  Checkbox,
} = designSystem;

function PageManagerTreeViewItem({
  id,
  name = '',
  title = '',
  password = undefined,
  iconGlyph,
  isActive = false,
  actions = [],
  isEmpty = false,
  isDragging = false,
  isDropTarget = false,
  selectModeEnabled = false,
  selectedItemsMap = {},
  onItemSelect = Function.prototype,
  onClick,
}) {
  const className = classNames('ws-page-manager-tree-view-item', {
    'ws-page-manager-tree-view-item--active': isActive,
    'ws-page-manager-tree-view-item--empty': isEmpty,
    'ws-page-manager-tree-view-item--dragging': isDragging,
    'ws-page-manager-tree-view-item--drop-target': isDropTarget,
    'ws-page-manager-tree-view-item--with-gap': !selectModeEnabled,
  });

  const portalRootNode = document.querySelector('.ws-page-manager-modal');

  const handleSelectionChange = () => {
    onItemSelect(id);
  };

  const getItem = () => (
    <>
      <span className="ws-page-manager-tree-view-item__logo">
        {!selectModeEnabled && (
          <Icon glyph={iconGlyph} size={20} {...(isActive && { type: 'accent' })} />
        )}
        {password && (
          <span className="ws-page-manager-tree-view-item__key-logo">
            <Icon glyph="key" size={12} type="light" strokeWidth={1.5} />
          </span>
        )}
      </span>

      <span className="ws-page-manager-tree-view-item__title-wrapper">
        <Paragraph isClipped {...(isActive && { appearance: 'accent' })}>
          <span translate="no">{name || title}</span>
        </Paragraph>
      </span>
    </>
  );

  return (
    <div className={className} onClick={selectModeEnabled ? handleSelectionChange : onClick}>
      {selectModeEnabled ? (
        <Checkbox
          id={id}
          value={id}
          label={getItem()}
          size="small"
          isLabelBold={false}
          checked={selectedItemsMap[id] || false}
          onChange={handleSelectionChange}
        />
      ) : (
        getItem()
      )}

      {actions.length > 0 && !selectModeEnabled && (
        <div className="ws-page-manager-tree-view-item__controls">
          <Dropdown
            placement="bottom-right"
            onClick={(e) => e.stopPropagation()}
            portalRootNode={portalRootNode}
          >
            <DropdownTrigger>
              <IconButton transparent glyph="more-2" size="medium" appearance="accent" />
            </DropdownTrigger>
            <DropdownContent>
              <ActionList withMobileBorder>
                {actions.map((action) => (
                  <React.Fragment key={action.id}>
                    {action.divider === 'before' && <ActionListDivider />}
                    <ActionListItem
                      onClick={action.onClick}
                      title={action.title}
                      iconGlyph={action.iconGlyph}
                      iconPlacement="left"
                      {...(action.iconType && { iconType: action.iconType })}
                      {...(action.component && { component: action.component })}
                    />
                    {action.divider === 'after' && <ActionListDivider />}
                  </React.Fragment>
                ))}
              </ActionList>
            </DropdownContent>
          </Dropdown>
        </div>
      )}
    </div>
  );
}

PageManagerTreeViewItem.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  title: PropTypes.string,
  password: PropTypes.string,
  iconGlyph: PropTypes.string.isRequired,
  isActive: PropTypes.bool,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      iconGlyph: PropTypes.string.isRequired,
      iconType: PropTypes.string,
      divider: PropTypes.oneOf(['before', 'after']),
      component: PropTypes.func,
      title: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
    })
  ),
  onClick: PropTypes.func.isRequired,
  isEmpty: PropTypes.bool,
  isDragging: PropTypes.bool,
  isDropTarget: PropTypes.bool,
  selectModeEnabled: PropTypes.bool,
  selectedItemsMap: PropTypes.shape({}),
  onItemSelect: PropTypes.func,
};

export default PageManagerTreeViewItem;
